// Soraka - Wish: globally heals the lowest-PERCENT-HP ally for AP * 1.2 + 120.
// Targeting by percent HP means a half-health tank (big pool) is prioritised over
// a near-full squishy. The heal is flat so high-max-HP tanks get less relative benefit.
// Soraka never moves (range 99) and never auto-attacks. Cannot heal herself.
// Heal fires at the moment the motes visually connect (end of float phase).

import { effectiveStats } from "../UnitStats";
import type { Ability, AbilityContext, AbilityHit } from "./Ability";

const HEAL_DELAY_MS = 1550; // BREATHE_MS + FLOAT_MS from SorakaWishVFX

export const sorakaWish: Ability = {
  id: "sorakaWish",
  displayName: "Wish",
  description: "Globally heals the lowest % HP ally for 120 + 120% AP. Targets allies only.",
  iconId: "SorakaR",

  targetShape(ctx) {
    const ally = findLowestPctHpAlly(ctx);
    return ally ? [ally.pos] : [ctx.caster.pos];
  },

  computeHits(ctx) {
    const ally = findLowestPctHpAlly(ctx);
    if (!ally) return [];
    const stats = effectiveStats(ctx.caster);
    const healAmount = Math.round(stats.ap * 1.2 + 120);
    const hit: AbilityHit = {
      target: ally,
      amount: 0,
      type: "none",
      heal: healAmount,
      sorakaHeal: true,
      delayMs: HEAL_DELAY_MS,
    };
    return [hit];
  },
};

// Lowest percent HP among living teammates (excluding Soraka herself).
// Using percent HP so a 50% tank is prioritised over a 90% squishy.
function findLowestPctHpAlly(ctx: AbilityContext) {
  const others = ctx.combat
    .livingUnitsOf(ctx.caster.team)
    .filter((u) => u.id !== ctx.caster.id && u.currentHp < effectiveStats(u).maxHp);
  if (others.length === 0) return null;
  return others.reduce((best, u) => {
    const uPct   = u.currentHp   / effectiveStats(u).maxHp;
    const bestPct = best.currentHp / effectiveStats(best).maxHp;
    return uPct < bestPct ? u : best;
  });
}
